import React, { useState } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { FiUser, FiUserCheck, FiUserX, FiFilter, FiSearch } from 'react-icons/fi';

const AdminUsers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Mock data
  const users = [
    { id: 1, name: 'Sarah Johnson', email: 'sarah.j@example.com', status: 'active', joinDate: '2023-03-15', transactions: 12 },
    { id: 2, name: 'Michael Chen', email: 'michael.c@example.com', status: 'active', joinDate: '2023-01-22', transactions: 8 },
    { id: 3, name: 'Emily Rodriguez', email: 'emily.r@example.com', status: 'inactive', joinDate: '2022-11-05', transactions: 3 },
    { id: 4, name: 'David Kim', email: 'david.k@example.com', status: 'active', joinDate: '2023-02-18', transactions: 15 },
    { id: 5, name: 'Jessica Lee', email: 'jessica.l@example.com', status: 'pending', joinDate: '2023-04-01', transactions: 0 },
    { id: 6, name: 'Robert Wilson', email: 'robert.w@example.com', status: 'active', joinDate: '2022-10-12', transactions: 21 },
    { id: 7, name: 'Amanda Garcia', email: 'amanda.g@example.com', status: 'inactive', joinDate: '2023-01-30', transactions: 5 },
    { id: 8, name: 'Thomas Brown', email: 'thomas.b@example.com', status: 'active', joinDate: '2022-12-24', transactions: 7 },
    { id: 9, name: 'Olivia Smith', email: 'olivia.s@example.com', status: 'pending', joinDate: '2023-03-28', transactions: 1 },
    { id: 10, name: 'Daniel Martinez', email: 'daniel.m@example.com', status: 'active', joinDate: '2023-02-05', transactions: 11 },
  ];
  
  // Filter users based on search term and status filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' || 
      user.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });
  
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'active':
        return <FiUserCheck className="text-green-500" size={16} />;
      case 'inactive':
        return <FiUserX className="text-gray-500" size={16} />;
      case 'pending':
        return <FiUser className="text-yellow-500" size={16} />;
      default:
        return <FiUser size={16} />;
    }
  };
  
  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h1 className="text-2xl font-bold">User Management</h1>
          
          <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiSearch className="text-gray-400" size={16} />
              </div>
              <input
                type="text"
                className="bg-foreground border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFilter className="text-gray-400" size={16} />
              </div>
              <select
                className="bg-foreground border border-border rounded-lg py-2 pl-10 pr-4 w-full focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            <button className="bg-primary text-white rounded-lg py-2 px-4 hover:bg-primary/90 transition-colors">
              Add New User
            </button>
          </div>
        </div>
        
        <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-card/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Join Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Transactions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-card/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-3">
                          {user.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-text/50 text-sm">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(user.status)}
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                          user.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : user.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text/70">
                      {user.joinDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text/70">
                      {user.transactions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        <button className="text-blue-500 hover:text-blue-700 transition-colors">
                          Edit
                        </button>
                        <button className="text-red-500 hover:text-red-700 transition-colors">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="px-6 py-8 text-center">
              <p className="text-text/50">No users found matching your search criteria.</p>
            </div>
          )}
          
          <div className="px-6 py-3 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text/70">
              Showing {filteredUsers.length} of {users.length} users
            </p>
            
            <div className="flex">
              <button className="px-3 py-1 rounded-md bg-card/50 text-sm mr-2 hover:bg-card-hover">
                Previous
              </button>
              <button className="px-3 py-1 rounded-md bg-primary text-white text-sm mr-2">
                1
              </button>
              <button className="px-3 py-1 rounded-md bg-card/50 text-sm">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers; 